<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row>
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!isEdit"
                  title="Add Task"
                ></SimplePageHeader>
                <SimplePageHeader
                  v-else
                  title="Edit Task"
                >
                  <template v-slot:right-buttons>
                    <v-switch
                      v-if="isAdminOrManager || isTaskResponsible"
                      v-model="complete"
                      label="Complete"
                      class="float-end"
                    ></v-switch>
                  </template>
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" align="end">
                <h2
                  v-if="complete"
                  class="text-success"
                >It is done!</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="name"
                      label="Name"
                      :rules="[required('Task Name')]"
                      required
                      outlined
                      :readonly="isReadonly"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="linkUrl"
                      label="Link Url"
                      :readonly="isReadonly"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="!isUserTodo">
                  <v-col cols="6">
                    <UsersDropDownList
                      label="Assigned To"
                      :userId="assignedToUserId"
                      :rules="[required('User')]"
                      :isRequired="true"
                      @userIdChange="handleUserIdChange"
                      :readonly="isReadonly"
                    ></UsersDropDownList>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-if="isReadonly"
                      label="Target Date"
                      v-model="targetDateFormatted"
                      readonly
                      outlined
                    >
                    </v-text-field>
                    <SimpleDatePicker
                      :minDateEnable="true"
                      :maxDateEnable="true"
                      :label="`Target Date`"
                      :value="targetDate"
                      :rules="[required('Target Date')]"
                      :isRequired="true"
                      @selected-date-change="handleTargetDateChange"
                      v-if="!isReadonly"
                    ></SimpleDatePicker>
                  </v-col>
                </v-row>
                <v-row v-if="isUserTodo">
                  <v-col cols="12" sm="4" md="5">
                    <v-text-field
                      v-if="isReadonly"
                      label="Target Date"
                      v-model="targetDateFormatted"
                      readonly
                      outlined
                    >
                    </v-text-field>
                    <SimpleDatePicker
                      :minDateEnable="true"
                      :maxDateEnable="true"
                      :label="`Target Date`"
                      :value="targetDate"
                      :rules="[required('Target Date')]"
                      :isRequired="true"
                      @selected-date-change="handleTargetDateChange"
                      v-if="!isReadonly"
                    ></SimpleDatePicker>
                  </v-col>
                  <v-col cols="12" sm="8" md="12">
                    <TodoGoalInput
                      :todoGoalId="todoGroupId"
                      :todoGoalIsRequired="false"
                      :showCreateTodoGoal="true"
                      :readonly="isReadonly"
                      @todoGoalIdChange="handleTodoGoalIdChange"
                      @todoGoalTargetDateChange="handleTargetDateChange"
                    ></TodoGoalInput>
                  </v-col>
                </v-row>
                <v-row v-if="!isUserTodo">
                  <v-col cols="12">
                    <TodoGoalInput
                      :todoGoalId="todoGroupId"
                      :todoGoalIsRequired="false"
                      :showCreateTodoGoal="true"
                      :readonly="isReadonly"
                      @todoGoalIdChange="handleTodoGoalIdChange"
                      @todoGoalTargetDateChange="handleTargetDateChange"
                    ></TodoGoalInput>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <v-row>
                  <v-col cols="12">
                    <quill-editor
                      class="editor"
                      ref="myQuillEditor"
                      v-model="description"
                      :options="editorOption"
                      style="height: auto;"
                      :disabled="isReadonly"
                    >
                      <div class="output ql-snow">
                        <div class="ql-editor" v-html="description"></div>
                      </div>
                    </quill-editor>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <span v-if="descriptionRequired" class="v-messages theme--light error--text">Description is Required.</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="isEditTodo">
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row>
              <v-col cols="12">
                <TodoNoteList
                  :todoIdIn="id"
                  :readonly="!(isAdminOrManager || isTaskResponsible)"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="end" v-if="!(isAdminOrManager || isTaskResponsible)">
        <v-col cols="6" md="3" class="pl-2">
          <v-btn
            block
            outlined
            depressed
            @click="cancel"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
        v-if="isAdminOrManager || isTaskResponsible"
        :isFormValid="isFormValid"
        @save="handleSaveTodo"
        @cancel="cancel"
      >
      </ButtonsSubmitForm>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { userAdminTodosRoute, userTodoDashboardRoute, notAuthorizedRoute } from '@/lib/constants/routes'
import { UsersDropDownList } from '@/components/users'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { SimplePageHeader } from '@/components/pageComponents'
import { required } from '@/lib/formValidations'
import { ButtonsSubmitForm } from '@/components/buttons'
import { TodoGoalInput } from '@/components/todoGoals'
import { TodoNoteList } from '@/components/todoNote'
import { formatDate } from '@/lib/display'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { MANAGER, ADMIN } from '@/lib/constants/securityRoles'

import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

export default {
  components: {
    UsersDropDownList,
    SimpleDatePicker,
    ButtonsSubmitForm,
    SimplePageHeader,
    TodoGoalInput,
    TodoNoteList,
    quillEditor
  },
  data: () => ({
    isFormValid: false,
    editForm: false,
    nameRules: [
      v => !!v || 'This field is required',
      v => (v && v.length <= 250) || 'This field must be less than 250 characters'
    ],
    editorOption: {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'script': 'sub' }, { 'script': 'super' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'direction': 'rtl' }],
          [{ 'size': ['small', false, 'large', 'huge'] }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'font': [] }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }]
        ]
      },
      placeholder: 'Enter the Task description here...'
    }
  }),
  computed: {
    ...mapFields('todo', [
      'name',
      'description',
      'linkUrl',
      'todoGroupId',
      'todoGroupName',
      'assignedToUserId',
      'targetDate',
      'complete',
      'completeAt',
      'completedBy',
      'createdBy',
      'updatedBy',
      'createdAt',
      'updatedAt'
    ]),
    id: {
      get () {
        return this.$route.params.id
      }
    },
    isEdit: {
      get () {
        return !!this.id
      }
    },
    currentUserId () {
      return getCurrentUser().id
    },
    isAdminOrManager () {
      return this.userHasRoles({
        user: getCurrentUser(),
        allowedRoles: [ADMIN, MANAGER]
      })
    },
    isTaskCreator () {
      return this.createdBy === this.currentUserId
    },
    isTaskResponsible () {
      return this.assignedToUserId === this.currentUserId
    },
    isReadonly () {
      return this.isEditTodo &&
        !(this.isAdminOrManager || this.isTaskCreator)
    },
    descriptionRequired () {
      return !this.description
    },
    isUserTodo () {
      return this.$route.fullPath.includes('userTodoDashBoard')
    },
    isEditTodo () {
      return this.id >= 0
    },
    editor () {
      return this.$refs.myQuillEditor.quill
    },
    returnTo () {
      let destinationTodoListPath = userTodoDashboardRoute.name
      if (!this.isUserTodo) {
        destinationTodoListPath = userAdminTodosRoute.name
      }
      return destinationTodoListPath
    },
    targetDateFormatted () {
      return this.formatDate(this.targetDate)
    }
  },
  methods: {
    ...mapActions('todo', [
      'getTodo',
      'saveTodo',
      'clearTodo'
    ]),
    ...mapActions('todoGroup', [
      'getTodoGroup'
    ]),
    async loadTodo () {
      await this.getTodo({ id: this.id })
    },
    handleSaveTodo () {
      if (this.$refs.form.validate() && this.description) {
        this.saveTodo().then(() => this.$router.push({ name: this.returnTo }))
      }
    },
    handleUserIdChange (value) {
      this.$nextTick(() => {
        this.assignedToUserId = value
      })
    },
    handleTargetDateChange (value) {
      this.$nextTick(() => {
        this.targetDate = value
      })
    },
    handleTodoGoalIdChange (value) {
      this.todoGroupId = value
      this.getTodoGroup({ id: value }).then(todoGroup => {
        this.handleTargetDateChange(todoGroup.targetDate)
      })
    },
    cancel () {
      this.$router.push({ name: this.returnTo })
    },
    getCurrentUser,
    userHasRoles,
    required,
    formatDate
  },
  async beforeMount () {
    if (this.isEdit) {
      await this.loadTodo()
      if (this.isUserTodo) {
        if (this.createdBy !== this.currentUserId && this.assignedToUserId !== this.currentUserId) {
          await this.$router.push({ name: notAuthorizedRoute.name })
        }
      }
    } else {
      this.clearTodo()
      if (this.isUserTodo) {
        this.assignedToUserId = this.currentUserId
      }
    }
  }
}
</script>
<style>
.text-success {
  color: limegreen;
}

.editor {
  height: 40rem;
  overflow: hidden;
}

.mt-5 {
  margin-top: 5px;
}

.ml-5 {
  margin-left: 5px;
}

.text-danger {
  color: Darkred;
}
</style>
